import { useWebSiteStore } from '@/stores/web-site.store'

export const WsMessageTypeEnum = Object.freeze({
  SYSTEM_BROADCAST: 'system.broadcast',
  SYSTEM_SUBSCRIBE: 'system.subscribe',
  SYSTEM_UNSUBSCRIBE: 'system.unsubscribe',
  SYSTEM_ERROR: 'system.error',
})

export default class BaseWs {
  constructor(path) {
    this.path = path
    this.subscriptions = []
    this.url = window.config.BASE_WS + path
    this.connectionAttempts = 0
  }

  createConnection() {
    const webSiteStore = useWebSiteStore()
    this.connection = new WebSocket(`${this.url}?authorization=${webSiteStore.accessToken}`)
    this.connection.onmessage = (event) => this.onInternalMessage(event)
    this.connection.onopen = (event) => this.onOnOpen(this, event)
    this.connection.onclose = (event) => this.onClose(this, event)
    this.connection.onerror = (event) => this.onError(this, event)
    return this.connection
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  async connectAsync() {
    if (this.connection && this.connection.readyState === WebSocket.CONNECTING) {
      this.connectionAttempts = ++this.connectionAttempts
      await this.delay(this.connectionAttempts * 500)
      await this.connectAsync(this.connection)
    }
  }

  async subscribeAsync(streams, onMessage, onSubscribe, onError) {
    if (!window.config.WEBSOCKET_ENABLED) {
      return
    }

    const self = this
    if (!this.connection || this.connection.readyState === WebSocket.CLOSED) {
      this.connection = this.createConnection()
    }

    await this.connectAsync()

    const subscription = { streams, onMessage, onSubscribe, onError }
    streams.forEach(stream => {
      if (!self.subscriptions.some(sub => sub.streams && sub.streams.includes(stream))) {
        this.connection.send(JSON.stringify({
          'type': WsMessageTypeEnum.SYSTEM_SUBSCRIBE,
          'streams': [stream]
        }))
      }
    })
    self.subscriptions.push(
      subscription
    )
    subscription.unsubscribe = () => self.unsubscribe(subscription)
    self.connectionAttempts = 1
    return subscription
  }

  unsubscribe(subscription) {
    this.subscriptions = this.subscriptions.filter(s => s !== subscription)

    if (subscription && subscription.streams) {
      subscription.streams.forEach(stream => {
        if (!this.subscriptions.some(sub => sub.streams && sub.streams.includes(stream))) {
          this.connection.send(JSON.stringify({
            'type': WsMessageTypeEnum.SYSTEM_UNSUBSCRIBE,
            'streams': [stream]
          }))
        }
      })
    }

    if (this.subscriptions.length === 0) {
      this.connection.close()
    }
  }

  destroy() {
    this.subscriptions.forEach(s => s.unsubscribe())
    this.connection.close()
  }

  onInternalMessage(response) {
    if (window.config.IS_DEBUG) {
      console.info(`Got websocket message from ${this.path}`)
      console.info(response)
    }
    const message = response && response.data ? JSON.parse(response.data) : {}
    if (message['type'] === WsMessageTypeEnum.SYSTEM_SUBSCRIBE) {
      this.subscriptions
        .filter(s => s.streams.includes(message['stream']) && s.onSubscribe)
        .forEach(s => {
          s.onSubscribe(this, response, message)
        })
    } else if (message['type'] === WsMessageTypeEnum.SYSTEM_ERROR) {
      this.subscriptions
        .filter(s => s.streams.includes(message['stream']) && s.onError)
        .forEach(s => {
          s.onError(this, response, message)
        })
    } else {
      this.subscriptions
        .filter(s => s.streams.includes(message['stream']) && s.onMessage)
        .forEach(s => {
          s.onMessage(this, response, message)
        })
    }
  }

  onMessage(response, message) {}

  onError(response, message) {
    if (window.config.IS_DEBUG) {
      console.error(`Got websocket error from ${this.path}`, response)
    }
  }

  onOnOpen(event) {
    if (window.config.IS_DEBUG) {
      console.info(`Successfully connected websocket at ${this.path} after ${this.connectionAttempts} connection attempts`)
    }
  }

  async onClose(response, message) {
    if (window.config.IS_DEBUG) {
      console.info(`Disconnnected connected websocket at ${this.path}`)
    }

    if (!response?.wasClean) {
      const waitingTime = Math.min(this.connectionAttempts * window.config.WEBSOCKET_RECONNECT_INTERVAL, 10 * window.config.WEBSOCKET_RECONNECT_INTERVAL)
      if (window.config.IS_DEBUG) {
        console.warn(`Connection attempt ${this.connectionAttempts} failed. Reconnecting in ${waitingTime} ms...`)
      }

      // Avoid infinite loop onclose
      this.connection.onclose = () => {}

      await this.delay(waitingTime)

      this.connectAsync(this.createConnection()).then(() => {
        this.subscriptions.forEach(s => {
          this.connection.send(JSON.stringify({
            type: 'system.subscribe',
            'streams': s.streams
          }))
        })
      })
    }
  }
}
